(ns io.daskul.webapp.calendar
  "Pure, framework-free calendar helpers shared by the day and week views.

   Everything here is clock-free (dates are passed in, never read from
   js/Date.now inside) and React-free, so it compiles under the
   :node-test build and is unit-tested directly (see calendar_test.cljs).
   The uix/Mantine view code lives in each product's calendar view; this
   is the logic layer — separate-logic-from-layout."
  (:require
   [io.daskul.api-contract.filters :as contract.filters]))

;; ============================================================================
;; date <-> YYYY-MM-DD (wall-date the user sees)
;; ============================================================================

(defn two-digit [n] (if (< n 10) (str "0" n) (str n)))

(defn format-ymd
  "Render a js/Date as YYYY-MM-DD from its *local* calendar components — the
   date the user sees in the calendar. Sent as ?date= / ?date-from= / ?date-to=,
   which the server matches directly against the wall-clock `date` column
   (#590 — no tz expansion)."
  [^js d]
  (str (.getFullYear d) "-"
       (two-digit (inc (.getMonth d))) "-"
       (two-digit (.getDate d))))

;; ============================================================================
;; Session-fetch query params
;;
;; A Sessions aggregate that allows `:date` + {:field :date :type :date-range}
;; accepts exactly:
;;
;;   ?date=YYYY-MM-DD               day view (equality)
;;   ?date-from=A&?date-to=B        week view (range, both bounds inclusive)
;;
;; The bound keys are NOT spelled out here — they come from the shared wire
;; contract (api-contract.filters), which the framework's API generator also
;; builds its accepted-param whitelist from. Query-param validation is strict,
;; so a local copy of the suffix convention that drifted from the server's is
;; a 400 "disallowed key" on every request — which is exactly what #648 was.
;; ============================================================================

(def ^:private date-field
  "The wall-clock column the calendar filters on (#590). Both the equality
   param and the range bounds derive from this one name."
  :date)

(defn day-params
  "Query params selecting the single wall-date `d` — the day view's window."
  [^js d]
  {date-field (format-ymd d)})

(defn week-params
  "Query params selecting the inclusive wall-date window [from..to] — the week
   view's Mon–Sun window, from the first and last of (week-days d)."
  [^js from-date ^js to-date]
  (let [[from-key to-key] (contract.filters/range-params date-field)]
    {from-key (format-ymd from-date)
     to-key   (format-ymd to-date)}))

;; ============================================================================
;; Week math — Monday-start
;; ============================================================================

(defn week-start
  "Monday of the week containing `d` (Monday-start). Returns a js/Date at
   local midnight carrying that Monday's calendar date.

   Pure Y-M-D arithmetic: JS Date normalizes day under/overflow, so month
   and year boundaries fall out for free (e.g. Mon of the week of Jan 1
   lands in the previous December)."
  [^js d]
  (let [dow (.getDay d)            ;; 0=Sun .. 6=Sat
        delta (mod (- dow 1) 7)]   ;; days back to Monday: Sun→6, Mon→0, Sat→5
    (js/Date. (.getFullYear d) (.getMonth d) (- (.getDate d) delta) 0 0 0 0)))

(defn shift-week
  "Shift `d` by `delta-weeks` whole weeks (±7 days each), at local midnight.
   Day-component arithmetic, so month/year boundaries normalize for free."
  [^js d delta-weeks]
  (js/Date. (.getFullYear d) (.getMonth d)
            (+ (.getDate d) (* 7 delta-weeks)) 0 0 0 0))

(defn week-days
  "The 7 days of the week containing `d`, Mon..Sun, each at local midnight."
  [^js d]
  (let [^js mon (week-start d)]
    (mapv (fn [i] (js/Date. (.getFullYear mon) (.getMonth mon)
                            (+ (.getDate mon) i) 0 0 0 0))
          (range 7))))

(defn week-range-label
  "Natural-language label for a week given its 7 day-dates (from week-days).
   Collapses shared month/year so it reads cleanly:
     same month → 'Mon 22 – Sun 28 Jun 2026'
     cross month → 'Mon 29 Jun – Sun 5 Jul 2026'
     cross year  → 'Mon 29 Dec 2025 – Sun 4 Jan 2026'"
  [days]
  (let [^js a (first days)
        ^js b (last days)
        mon (fn [^js d] (.toLocaleDateString d "en-US" #js {:month "short"}))
        same-month? (and (= (.getFullYear a) (.getFullYear b))
                         (= (.getMonth a) (.getMonth b)))
        same-year? (= (.getFullYear a) (.getFullYear b))
        left (cond
               same-month? (str "Mon " (.getDate a))
               same-year? (str "Mon " (.getDate a) " " (mon a))
               :else (str "Mon " (.getDate a) " " (mon a) " " (.getFullYear a)))
        right (str "Sun " (.getDate b) " " (mon b) " " (.getFullYear b))]
    (str left " – " right)))

;; ============================================================================
;; Overlap layout — interval partition with column recycling
;; ============================================================================
;;
;; Assigns :col + :col-count to each session so concurrent blocks render
;; side-by-side without stacking. Active-set sweep — the standard "Google
;; Calendar" behavior:
;;
;;   Pass 1 (assign :col): sort by start. For each session, drop from the
;;   active-set any session whose end is ≤ this session's start (no longer
;;   overlapping, so the column frees up). Assign col = lowest unused.
;;
;;   Pass 2 (assign :col-count): for each session, col-count = 1 + max :col
;;   across every session that time-overlaps with it.
;;
;; Columns are RECYCLED as predecessors end: a 10:00 session can claim col 0
;; if the 9:00–10:00 session in col 0 has ended, even while a longer
;; concurrent session (9:30–10:30) is still alive.

(defn assign-overlap-layout
  "[sessions] -> [sessions each with :col + :col-count] (see ns comment).
   A non-overlapping session gets col 0, col-count 1 (full width)."
  [sessions]
  ;; #590: sessions carry a naive wall clock (:start-time / :end-time as
  ;; zero-padded "HH:MM:SS"), so plain string `compare` is chronological — no
  ;; epoch/tz needed. Callers only ever pass same-day sessions (the day view's
  ;; one-day fetch, or one day-column via layout-week), so ordering by
  ;; time-of-day alone is total.
  (let [sorted (vec (sort-by :start-time sessions))
        ;; Pass 1: greedy column assignment with an active-set.
        with-col
        (loop [remaining sorted
               active []
               result []]
          (if (empty? remaining)
            result
            (let [s (first remaining)
                  active' (filterv #(pos? (compare (:end-time %) (:start-time s)))
                                   active)
                  used (set (map :col active'))
                  col (first (remove used (range)))
                  s+col (assoc s :col col)]
              (recur (rest remaining)
                     (conj active' s+col)
                     (conj result s+col)))))
        ;; Two intervals [as,ae) and [bs,be) overlap iff as < be AND bs < ae.
        overlaps?
        (fn [a b]
          (and (neg? (compare (:start-time a) (:end-time b)))
               (neg? (compare (:start-time b) (:end-time a)))))]
    ;; Pass 2: per-session :col-count from the max :col among overlapping siblings.
    (mapv (fn [s]
            (let [cc (inc (apply max 0 (map :col (filter #(overlaps? s %) with-col))))]
              (assoc s :col-count cc)))
          with-col)))

(def ^:private day-name
  "js/Date#getDay is 0=Sunday. Tokens match Class :weekdays and the `operating-days` config (#715) — one
   weekday vocabulary across the product, not two."
  ["sun" "mon" "tue" "wed" "thu" "fri" "sat"])

(defn visible-week-days
  "The subset of `days` a week grid should actually render (#715).

   A day survives if the branch is open on it, OR if it has sessions. Closed
   AND empty is the only thing dropped.

   The second half of that rule is the important one. A make-up or a one-off
   catch-up booked on a normally-shut Sunday is exactly the session an admin
   most needs to see; hiding the column because the branch is nominally closed
   would lose it silently, which is worse than the wasted width this exists to
   reclaim.

   `open-day-names` is a set of lowercase names. Empty or nil means nothing is
   known — every day renders, rather than none. Unset config is open-by-default
   all the way up the stack: a branch has to opt INTO being shut.

   Returns `days` unchanged when nothing would be dropped, so the common
   fully-open case is identity."
  [days sessions-by-day open-day-names]
  (if (empty? open-day-names)
    days
    (filterv (fn [^js d]
               (or (contains? open-day-names (nth day-name (.getDay d)))
                   (seq (get sessions-by-day (format-ymd d)))))
             days)))

(defn layout-week
  "Bucket `sessions` into day-columns keyed by their wall-clock `:date`
   (#590 — already the school-local calendar day, no tz needed) and run
   `assign-overlap-layout` independently per day, so concurrent sessions split
   side-by-side only against SAME-day siblings — a busy Monday never widens
   Tuesday's column. Returns {day-key(YYYY-MM-DD) -> [laid-out sessions]}."
  [sessions]
  (reduce-kv (fn [m k ss] (assoc m k (assign-overlap-layout ss)))
             {}
             (group-by :date sessions)))
